/**
 * 라이브 업데이트(OTA) — 웹 번들 다운로드·적용
 *
 * - 어떤 번들을 쓸지는 서버(CheckClientVersion 응답의 ota)가 정하고, 여기서는 그 지시대로 받기/적용만 함
 * - 새 번들 적용(앱 재시작)은 앱을 새로 실행했을 때 로딩 화면에서만 함
 *   플러그인의 next()는 앱이 백그라운드로 갈 때(카메라·갤러리 열기 포함) 바로 적용해 작성 중인 화면이 날아가므로 쓰지 않음
 * - REACT_APP_USE_VERSION_POLICY=true 인 네이티브 앱에서만 동작 (웹/PWA 는 해당 없음)
 */
import { Capacitor } from '@capacitor/core';
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import Commons from './Common';

/** 이 웹 번들의 버전 (빌드 시각 — craco.config.js, scripts/ota/build-ota.js 에서 주입) */
export const BUNDLE_VERSION = process.env.REACT_APP_BUNDLE_VERSION || '';

const BUILTIN_KEY = 'liveUpdateBuiltin'; // { native, version } 스토어 앱에 내장된 번들
const APPLYING_KEY = 'liveUpdateApplying'; // { version } 적용(재시작) 직전 기록 — 롤백 감지용
const FAILED_KEY = 'liveUpdateFailed'; // { version, count, at, blocked } 실패한 번들
const MAX_DOWNLOAD_FAILS = 3;
const DOWNLOAD_RETRY_MS = 6 * 60 * 60 * 1000; // 다운로드 3번 실패하면 6시간 뒤 다시 시도

let downloadingVersion = null;
let readyPrompt = null; // { version, releaseNote, apply }
const promptListeners = new Set();

export const isLiveUpdateEnabled = () =>
  Capacitor.isNativePlatform() &&
  process.env.REACT_APP_USE_VERSION_POLICY === 'true' &&
  !!BUNDLE_VERSION;

/**
 * 이 번들이 정상 실행됐다고 플러그인에 알림 — 앱 시작 직후, 네트워크 요청 전에 호출
 * (15초 안에 호출되지 않으면 플러그인이 이전 번들로 롤백)
 */
export async function markAppReady() {
  if (!Capacitor.isNativePlatform()) return;

  try {
    await CapacitorUpdater.notifyAppReady();
  } catch (e) {
    console.warn('[LiveUpdate] notifyAppReady 실패', e?.message || e);
  }

  CheckPreviousApply();
}

/** 직전에 적용한 번들로 떠 있지 않으면 = 새 번들이 롤백된 것 → 같은 번들을 다시 받지 않도록 막음 */
function CheckPreviousApply() {
  const applying = Commons.Storages('get', APPLYING_KEY);
  if (!applying?.version) return;

  Commons.Storages('remove', APPLYING_KEY);
  if (applying.version !== BUNDLE_VERSION) {
    console.warn(
      `[LiveUpdate] ${applying.version} 적용 실패(롤백됨) — 이 번들은 다시 받지 않음`
    );
    Commons.Storages('set', FAILED_KEY, {
      version: applying.version,
      count: MAX_DOWNLOAD_FAILS,
      at: Date.now(),
      blocked: true,
    });
  }
}

/** CheckClientVersion 요청에 붙일 OTA 정보 (해당 없으면 빈 객체 → 서버는 OTA 판단을 하지 않음) */
export async function getLiveUpdateRequestInfo() {
  if (!isLiveUpdateEnabled()) return {};

  try {
    const { bundle, native } = await CapacitorUpdater.current();
    if (bundle?.id === 'builtin') {
      Commons.Storages('set', BUILTIN_KEY, { native, version: BUNDLE_VERSION });
    }
    const builtin = Commons.Storages('get', BUILTIN_KEY);
    // 기기 ID 를 못 읽어도 OTA 는 받도록 (단계적 배포 대상에서만 빠짐)
    const deviceId = await getLiveUpdateDeviceId();

    return {
      bundleVersion: BUNDLE_VERSION,
      // 스토어 앱이 바뀌었으면(native 다름) 예전 기록은 쓰지 않음
      builtinBundleVersion: builtin?.native === native ? builtin.version : '',
      deviceId,
    };
  } catch (e) {
    console.warn('[LiveUpdate] 현재 번들 정보 조회 실패', e?.message || e);
    return {};
  }
}

/**
 * 서버 지시(ota) 처리
 * - atStartup(앱 시작, 로딩 화면 중): 받아 둔 번들이 있으면 바로 적용(재시작), BUILTIN 이면 내장 번들로 되돌림
 * - 그 외(앱 복귀 시 확인): 받기만 하고 적용은 다음 실행 때
 * - 다운로드는 기다리지 않음. IMMEDIATE 번들은 다 받으면 안내창으로 지금 적용할지 물음
 */
export async function handleLiveUpdate(ota, { atStartup = false } = {}) {
  if (!isLiveUpdateEnabled() || !ota?.action) return;

  try {
    if (ota.action === 'BUILTIN') {
      const { bundle } = await CapacitorUpdater.current();
      if (bundle?.id === 'builtin') {
        if (atStartup) await CleanupBundles();
      } else if (atStartup) {
        console.info('[LiveUpdate] 서버 지시로 내장 번들로 되돌림(재시작)');
        await CapacitorUpdater.reset();
      }
      return;
    }

    const { bundleVersion, bundleUrl, checksum } = ota;
    if (ota.action !== 'UPDATE' || !bundleVersion || !bundleUrl || !checksum) {
      return;
    }

    if (bundleVersion === BUNDLE_VERSION) {
      if (atStartup) await CleanupBundles();
      return;
    }
    if (IsBlocked(bundleVersion)) return;

    const ready = await FindDownloadedBundle(bundleVersion);
    if (ready) {
      if (atStartup) {
        await ApplyBundle(ready);
      } else if (ota.applyMode === 'IMMEDIATE') {
        ShowReadyPrompt(ready, ota);
      }
      return;
    }

    DownloadBundle(ota);
  } catch (e) {
    console.warn('[LiveUpdate] 업데이트 처리 실패', e?.message || e);
  }
}

async function FindDownloadedBundle(version) {
  const { bundles } = await CapacitorUpdater.list();
  return bundles.find(
    (b) =>
      b.version === version &&
      (b.status === 'pending' || b.status === 'success')
  );
}

async function DownloadBundle(ota) {
  if (downloadingVersion) return;
  downloadingVersion = ota.bundleVersion;

  try {
    console.info(`[LiveUpdate] ${ota.bundleVersion} 다운로드 시작`);
    const bundle = await CapacitorUpdater.download({
      url: ota.bundleUrl,
      version: ota.bundleVersion,
      checksum: ota.checksum,
    });
    Commons.Storages('remove', FAILED_KEY);
    console.info(
      `[LiveUpdate] ${ota.bundleVersion} 다운로드 완료 — 다음 실행 때 적용`
    );

    await CleanupBundles(bundle.id);
    if (ota.applyMode === 'IMMEDIATE') ShowReadyPrompt(bundle, ota);
  } catch (e) {
    console.warn(
      `[LiveUpdate] ${ota.bundleVersion} 다운로드 실패`,
      e?.message || e
    );
    RecordDownloadFail(ota.bundleVersion);
  } finally {
    downloadingVersion = null;
  }
}

/**
 * 기기 저장공간 정리 — 지금 쓰는 번들, 방금 받은 번들, 직전에 정상 실행됐던 번들 1개만 남김
 * (직전 번들을 남겨 두면 긴급 중단 시 다시 받지 않고 다음 실행에 바로 되돌릴 수 있음)
 */
async function CleanupBundles(keepId) {
  try {
    const { bundle: current } = await CapacitorUpdater.current();
    const { bundles } = await CapacitorUpdater.list();
    const others = bundles.filter(
      (b) =>
        b.id !== 'builtin' &&
        b.id !== current?.id &&
        b.id !== keepId &&
        b.status !== 'downloading'
    );
    const previous = others
      .filter((b) => b.status === 'success')
      .sort((a, b) =>
        String(b.downloaded).localeCompare(String(a.downloaded))
      )[0];

    for (const b of others) {
      if (b.id !== previous?.id) await CapacitorUpdater.delete({ id: b.id });
    }
  } catch (e) {
    console.warn('[LiveUpdate] 이전 번들 정리 실패', e?.message || e);
  }
}

/** 번들 적용 — 앱이 즉시 재시작되므로 이 함수 뒤의 코드는 실행되지 않음 */
async function ApplyBundle(bundle) {
  console.info(`[LiveUpdate] ${bundle.version} 적용(재시작)`);
  Commons.Storages('set', APPLYING_KEY, { version: bundle.version });
  await CapacitorUpdater.set({ id: bundle.id });
}

function RecordDownloadFail(version) {
  const failed = Commons.Storages('get', FAILED_KEY);
  const count = failed?.version === version ? (failed.count || 0) + 1 : 1;
  Commons.Storages('set', FAILED_KEY, { version, count, at: Date.now() });
}

function IsBlocked(version) {
  const failed = Commons.Storages('get', FAILED_KEY);
  if (failed?.version !== version) return false;
  if (failed.blocked) return true;

  return (
    failed.count >= MAX_DOWNLOAD_FAILS &&
    Date.now() - failed.at < DOWNLOAD_RETRY_MS
  );
}

function ShowReadyPrompt(bundle, ota) {
  readyPrompt = {
    version: bundle.version,
    releaseNote: ota.releaseNote || '',
    apply: () => ApplyBundle(bundle),
  };
  promptListeners.forEach((listener) => listener(readyPrompt));
}

/** IMMEDIATE 번들 준비 완료 안내 구독 (LiveUpdateDialog) — 해제 함수 반환 */
export function subscribeLiveUpdatePrompt(listener) {
  promptListeners.add(listener);
  if (readyPrompt) listener(readyPrompt);

  return () => {
    promptListeners.delete(listener);
  };
}

/** 테스트 기기 등록(TB_CLIENT_BUNDLE_POLICY.TEST_DEVICE_IDS)용 기기 ID */
export async function getLiveUpdateDeviceId() {
  if (!Capacitor.isNativePlatform()) return '';

  try {
    const { deviceId } = await CapacitorUpdater.getDeviceId();
    return deviceId || '';
  } catch (e) {
    return '';
  }
}
